import i18next from 'i18next';
import {
  AppsV1NavigationSuccessResponse,
  AppsV1NavigationMenuItem,
  NavigationItem,
} from '../../types';
import { getReplatformedURL } from '../../utils';
import { mapIdToIcon } from '../utils/mappers';
import {
  CUSTOM_OBJECTS_MENU_ID,
  PLATFORM_MENU_GROUP_ID,
} from '../../hooks/useCustomObjectDefinition';
import { insertNavigationItems } from '../../hooks/useBillingNavigation';
import { BaseNavigationTransformer } from './base-navigation-transformer';
import {
  buildProductsPopover,
  ProductsCatalogMenuOptions,
} from './build-products-popover';
import MenuPathMap from '../../MenuPathMap';
import { convertToBillingUrl } from '../../utils/getBillingBaseUrl';

const SupplementalMenuPathMap = {
  'Zuora/Customers/Subscriptions': ['/platform/subscriptions'],
  'Zuora/Customers/Orders': ['/orders#', '/orders'],
  'Zuora/Customers/Amendments': ['/apps/Amendment.do'],
  'Zuora/Billing/BillRuns': ['/billing/bill-runs'],
  'Zuora/Billing/CreditDebitMemos': ['/platform/apps/com_zuora/memos'],
  'Zuora/Payments/PaymentsPayments': ['/apps/ARPayment.do'],
  'Zuora/Payments/Refunds': ['/apps/ARRefund.do'],
  'Zuora/Reporting/Spotlight': ['/reporting'],
  'Zuora/Analytics/Dashboard': ['/platform/analytics/card'],
  'Zuora/Analytics/Explorer': ['/platform/analytics/dataset'],
  'Zuora/Workflow/RulesEngine': [
    '/platform/apps/custom-logic',
    '/platform/apps/rules-engine',
  ],
  'Zuora/Workflow/EventsAndNotifications': [
    '/platform/apps/com_zuora/notificationdefinition',
    '/platform/apps/com_zuora/emailtemplate',
    '/platform/apps/com_zuora/callouttemplate',
    '/platform/apps/com_zuora/customevent',
    '/apps/NotificationSettings.do',
  ],
  'Zuora/Workflow/ObjectManager': ['/platform/apps/com_zuora/processes'],
};

export class BillingNavigationTransformer extends BaseNavigationTransformer {
  async transform(
    billingData: AppsV1NavigationSuccessResponse,
    customObjectNavItems?: AppsV1NavigationMenuItem[],
    addSolutionMenuItems?: (
      menus: AppsV1NavigationMenuItem[],
    ) => AppsV1NavigationMenuItem[],
    navigationInsertionItems?: any[], // Pass this from your component
    productsCatalogMenu?: ProductsCatalogMenuOptions | null,
  ): Promise<{
    secondaryItems: NavigationItem[];
    bottomItems: NavigationItem[];
  }> {
    const baseMenus = billingData.menus;

    // Step 2: Apply getMenuItemsInOrder (insert custom objects in the right position)
    const menusWithCustomObjects = this.getMenuItemsInOrder(
      baseMenus,
      customObjectNavItems || [],
    );

    // Step 3: Apply addSolutionMenuItems (adds Appsmith solutions)
    const menusWithSolutions = addSolutionMenuItems
      ? addSolutionMenuItems(menusWithCustomObjects)
      : menusWithCustomObjects;

    // Step 4: Apply insertNavigationItems
    const finalMenus = navigationInsertionItems
      ? insertNavigationItems({
          menus: menusWithSolutions,
          itemsToInsert: navigationInsertionItems,
        })
      : menusWithSolutions;

    console.log('Final transformed menus:', finalMenus);

    const itemsByParent = this.groupItemsByParent(finalMenus);

    return Promise.resolve({
      secondaryItems: await this.transformSecondaryItems(
        itemsByParent,
        productsCatalogMenu,
      ),
      bottomItems: this.transformBottomItems(billingData.settings),
    });
  }

  private getMenuItemsInOrder(
    menus: AppsV1NavigationMenuItem[] | undefined,
    customObjectNavItems: AppsV1NavigationMenuItem[],
  ): AppsV1NavigationMenuItem[] {
    if (!menus) {
      return [];
    }

    const financeMenuGroupIndex =
      menus?.findIndex((item) => item.id === 'Zuora/Finance') ?? -1;

    //Index of last Platform Menu Menu for reordering
    const platformMenuGroupIndex =
      menus?.findIndex((item) => item.id === PLATFORM_MENU_GROUP_ID) ?? -1;

    const customObjectNavParentItem = {
      id: CUSTOM_OBJECTS_MENU_ID,
      label: i18next.t('GlobalNav.Sections.CustomObjects'),
      parentId: null,
      url: null,
    };

    const placeCustomObjectsMenu = (
      position: number,
    ): AppsV1NavigationMenuItem[] => [
      ...menus.slice(0, position),
      customObjectNavParentItem,
      ...customObjectNavItems,
      ...menus.slice(position),
    ];

    if (platformMenuGroupIndex >= 0) {
      if (financeMenuGroupIndex >= 0) {
        return placeCustomObjectsMenu(financeMenuGroupIndex + 1);
      }
      return placeCustomObjectsMenu(platformMenuGroupIndex);
    }

    return [...menus, customObjectNavParentItem];
  }

  private insertCustomObjectsIntoMenus(
    menus: AppsV1NavigationMenuItem[],
    customObjectNavItems: AppsV1NavigationMenuItem[],
  ): AppsV1NavigationMenuItem[] {
    if (!customObjectNavItems.length) {
      return menus;
    }

    const financeMenuGroupIndex = menus.findIndex(
      (item) => item.id === 'Zuora/Finance',
    );

    const platformMenuGroupIndex = menus.findIndex(
      (item) => item.id === PLATFORM_MENU_GROUP_ID,
    );

    const customObjectNavParentItem = {
      id: CUSTOM_OBJECTS_MENU_ID,
      label: i18next.t('GlobalNav.Sections.CustomObjects'),
      parentId: null,
      url: null,
    };

    const insertCustomObjectsAt = (
      position: number,
    ): AppsV1NavigationMenuItem[] => [
      ...menus.slice(0, position),
      customObjectNavParentItem,
      ...customObjectNavItems,
      ...menus.slice(position),
    ];

    if (platformMenuGroupIndex >= 0) {
      if (financeMenuGroupIndex >= 0) {
        return insertCustomObjectsAt(financeMenuGroupIndex + 1);
      }
      return insertCustomObjectsAt(platformMenuGroupIndex);
    }

    return [...menus, customObjectNavParentItem];
  }

  private groupItemsByParent(menus: AppsV1NavigationMenuItem[]) {
    const itemsByParent = new Map<string | null, AppsV1NavigationMenuItem[]>();
    itemsByParent.set(null, []);

    menus.forEach((item) => {
      const parentId = item.parentId || null;
      if (!itemsByParent.has(parentId)) {
        itemsByParent.set(parentId, []);
      }
      itemsByParent.get(parentId)?.push(item);
    });

    return itemsByParent;
  }

  private async transformSecondaryItems(
    itemsByParent: Map<string | null, AppsV1NavigationMenuItem[]>,
    productsCatalogMenu?: ProductsCatalogMenuOptions | null,
  ): Promise<NavigationItem[]> {
    const items = (itemsByParent.get(null) || [])
      .map((item) => {
        const childItems = itemsByParent.get(item.id) || [];
        const useProductsCatalogMenu =
          item.id === 'Zuora/Products' && !!productsCatalogMenu;
        const hasChildren = childItems.length > 0 || useProductsCatalogMenu;

        if (!hasChildren && item.url === null) {
          return undefined;
        }

        const navItem: NavigationItem = {
          label: item.label,
          icon: mapIdToIcon(item.id),
          originalHref: item.url || undefined, // Store original
          menuPath: [
            ...(SupplementalMenuPathMap[item.id] || []),
            ...(MenuPathMap[item.id] ? [MenuPathMap[item.id]] : []),
          ],
          expandable: hasChildren,
          endChip: item.endChip,
          endChipType: item.endChipType,
          id: item.id,
        };

        const isCustomObjectMenuGroup = item.id === CUSTOM_OBJECTS_MENU_ID;

        if (isCustomObjectMenuGroup) {
          navItem.icon = 'customObjects';
        }

        // Special case handling
        if (item.id === 'Zuora/Billing') {
          navItem.icon = 'page';
        }

        if (useProductsCatalogMenu) {
          const productCatalogFromApi = childItems.find(
            (child) => child.id === 'Zuora/Products/ProductCatalog',
          );
          const catalogPopover = buildProductsPopover({
            ...productsCatalogMenu,
            productCatalogOriginalHref:
              productCatalogFromApi?.url ||
              productsCatalogMenu?.productCatalogOriginalHref,
          });

          // Preserve API-returned items in the Product Catalog column.
          // Items not explicitly included in the catalog menu (e.g., Features for users
          // without FeaturesEntitlements permission) are appended to maintain backward compatibility.
          const catalogItemIds = new Set(
            catalogPopover.items.map((item) => item.id),
          );

          const preservedItems = childItems.filter(
            (child) => child.url && !catalogItemIds.has(child.id),
          );

          if (preservedItems.length > 0) {
            catalogPopover.items.push(
              ...preservedItems.map<NavigationItem>((child) => ({
                label: child.label,
                id: child.id,
                originalHref: child.url || undefined,
                menuPath: [
                  ...(SupplementalMenuPathMap[child.id] || []),
                  ...(MenuPathMap[child.id] ? [MenuPathMap[child.id]] : []),
                ],
                icon: undefined,
                expandable: false,
                pinnable: true,
                endChip: child.endChip,
                endChipType: child.endChipType,
              })),
            );
          }

          navItem.popover = catalogPopover;
        } else if (hasChildren) {
          navItem.popover = {
            label: navItem.label,
            id: `${navItem.id}-popover`,
            items: childItems.map<NavigationItem>((child) => ({
              label: child.label,
              id: child.id,
              originalHref: child.url || undefined, // Store original
              menuPath: [
                ...(SupplementalMenuPathMap[child.id] || []),
                ...(MenuPathMap[child.id] ? [MenuPathMap[child.id]] : []),
              ],
              icon: undefined,
              expandable: false,
              pinnable: true,
              endChip: child.endChip,
              endChipType: child.endChipType,
            })),
          };
        }

        return navItem;
      })
      .filter((item): item is NavigationItem => item !== undefined);

    // Transform URLs for all items
    return await this.transformUrls(items);
  }

  // Add URL transformation method to your BillingNavigationTransformer
  private async transformUrls(
    items: NavigationItem[],
  ): Promise<NavigationItem[]> {
    return Promise.all(
      items.map(async (item) => {
        const transformedItem = { ...item };

        // Transform main href
        if (item.originalHref) {
          try {
            transformedItem.href = await getReplatformedURL(item.originalHref);
          } catch (error) {
            console.error('Failed to transform URL:', item.originalHref, error);
            transformedItem.href = item.originalHref; // Fallback to original
          }
        }

        // Transform popover items (first column + additional columns)
        if (item.popover) {
          const transformPopoverItem = async (
            popoverItem: NavigationItem,
          ): Promise<NavigationItem> => {
            if (!popoverItem.originalHref) {
              return popoverItem;
            }
            try {
              return {
                ...popoverItem,
                href: await getReplatformedURL(popoverItem.originalHref),
              };
            } catch (error) {
              console.error(
                'Failed to transform popover URL:',
                popoverItem.originalHref,
                error,
              );
              return {
                ...popoverItem,
                href: popoverItem.originalHref, // Fallback
              };
            }
          };

          const transformedPopoverItems = item.popover.items
            ? await Promise.all(item.popover.items.map(transformPopoverItem))
            : item.popover.items;

          const transformedAdditionalItems = item.popover.additionalItems
            ? await Promise.all(
                item.popover.additionalItems.map(async (column) => ({
                  ...column,
                  items: await Promise.all(
                    column.items.map(transformPopoverItem),
                  ),
                })),
              )
            : item.popover.additionalItems;

          transformedItem.popover = {
            ...item.popover,
            items: transformedPopoverItems,
            additionalItems: transformedAdditionalItems,
          };
        }

        return transformedItem;
      }),
    );
  }

  private transformBottomItems(settings: any[]): NavigationItem[] {
    // Separate help, settings, and other items
    let helpItem: NavigationItem | undefined;
    let settingsItem: NavigationItem | undefined;
    const otherItems: NavigationItem[] = [];

    settings.forEach((setting) => {
      const id = setting.id;

      if (setting.parentId === 'Zuora/Settings') {
        return; // skip
      }

      if (id === 'Zuora/HelpAndTraining') {
        helpItem = {
          label: i18next.t('GlobalNav.Sections.Help'),
          icon: mapIdToIcon(setting.id),
          id: setting.id,
          popover: this.createHelpPopover(i18next.t('GlobalNav.Sections.Help')),
        };
        return;
      }

      if (id === 'Zuora/Settings') {
        settingsItem = {
          label: i18next.t('GlobalNav.Sections.AppSettings'),
          icon: mapIdToIcon(setting.id),
          appName: 'app-settings',
          id: setting.id,
        };
        return;
      }

      if (!['Zuora/Profile', 'Zuora/Logout'].includes(id)) {
        otherItems.push({
          label: setting.label,
          icon: mapIdToIcon(setting.id),
          href: convertToBillingUrl(setting.url),
          id: setting.id,
        });
      }
    });

    const feedBackItem = this.createFeedbackItem();

    const items: NavigationItem[] = [feedBackItem];
    if (helpItem) items.push(helpItem);
    if (settingsItem) items.push(settingsItem);
    items.push(...otherItems);

    return items;
  }
}
